import React, { ReactNode } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

function Icon({ name, className }: { name: string; className?: string }) {
  return <span className={cn('material-symbols-outlined', className)}>{name}</span>;
}

// Accent → gradient bg, border, icon container, hover line
const accentMap: Record<string, { card: string; border: string; iconBg: string; iconText: string; line: string }> = {
  'bg-warning':  { card: 'bg-gradient-to-br from-amber-50 to-amber-50/30',       border: 'border-amber-200/60',        iconBg: 'bg-amber-100',        iconText: 'text-amber-600',        line: 'bg-amber-400' },
  'bg-error':    { card: 'bg-gradient-to-br from-rose-50 to-rose-50/30',         border: 'border-rose-200/60',         iconBg: 'bg-rose-100',         iconText: 'text-rose-600',         line: 'bg-rose-400' },
  'bg-success':  { card: 'bg-gradient-to-br from-emerald-50 to-emerald-50/30',   border: 'border-emerald-200/60',      iconBg: 'bg-emerald-100',      iconText: 'text-emerald-600',      line: 'bg-emerald-400' },
  'bg-asesor':   { card: 'bg-gradient-to-br from-steel-azure-50 to-fresh-sky-50/30', border: 'border-steel-azure-200/60', iconBg: 'bg-steel-azure-100', iconText: 'text-steel-azure-600', line: 'bg-steel-azure-400' },
  'bg-admin':    { card: 'bg-gradient-to-br from-steel-azure-50 to-steel-azure-100/30', border: 'border-steel-azure-200/60', iconBg: 'bg-steel-azure-100', iconText: 'text-steel-azure-700', line: 'bg-steel-azure-500' },
  'bg-cliente':  { card: 'bg-gradient-to-br from-fresh-sky-50 to-fresh-sky-100/30',  border: 'border-fresh-sky-200/60',  iconBg: 'bg-fresh-sky-100',    iconText: 'text-fresh-sky-600',    line: 'bg-fresh-sky-400' },
};

const defaultAccent = { card: 'bg-gradient-to-br from-slate-50 to-slate-50/30', border: 'border-slate-200/60', iconBg: 'bg-slate-100', iconText: 'text-slate-500', line: 'bg-slate-400' };

interface MetricCardProps {
  titulo: string;
  valor: string | number;
  descripcion?: string;
  icono?: string | ReactNode;
  trend?: {
    valor: number;
    positivo: boolean;
  };
  accent?: string;
  onClick?: () => void;
  href?: string;
  className?: string;
}

export default function MetricCard({
  titulo,
  valor,
  descripcion,
  icono,
  trend,
  accent,
  onClick,
  href,
  className = '',
}: MetricCardProps) {
  const config = (accent && accentMap[accent]) || defaultAccent;

  const CardContent = () => (
    <div className={cn('metric-card group h-full flex flex-col min-h-[130px]', config.card, config.border, className)}>
      {/* Top accent line */}
      <div className={`absolute top-0 left-6 right-6 h-[2px] ${config.line} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

      <div className="flex items-start justify-between flex-1">
        <div className="flex-1 flex flex-col min-w-0">
          <p className="stat-label mb-3">{titulo}</p>
          <p className="stat-value animate-count-up">{typeof valor === 'number' ? valor.toLocaleString('es-ES') : valor}</p>
          <div className="mt-auto pt-3 flex items-center gap-3">
            {descripcion && (
              <p className="text-sm text-calm-muted">{descripcion}</p>
            )}
            {trend && (
              <span className={trend.positivo ? 'stat-change-up' : 'stat-change-down'}>
                {trend.positivo ? <Icon name="trending_up" className="text-sm" /> : <Icon name="trending_down" className="text-sm" />}
                {Math.abs(trend.valor)}%
              </span>
            )}
          </div>
        </div>
        {icono && (
          <div className={cn('ml-4 flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors', config.iconBg, config.iconText)}>
            {typeof icono === 'string' ? (
              <span className="text-xl">{icono}</span>
            ) : (
              icono
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="block">
        <CardContent />
      </Link>
    );
  }

  if (onClick) {
    return (
      <div onClick={onClick} className="cursor-pointer">
        <CardContent />
      </div>
    );
  }

  return <CardContent />;
}
